import { Component, computed, DestroyRef, effect, inject, input, OnInit, output, signal, ViewChild } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { ProductCardTooltipService } from "@components/product-card/product-card-tooltip.service";
import { ProductImageComponent } from "@components/product-image/product-image.component";
import { DisplayNamePipe, LocalizeProductNamePipe, Product } from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { UserService } from "@domain/user/user.service";
import { CheckBoxControl } from "@odx/angular/cdk/checkbox-control";
import { distinctUntilChanged, map } from "rxjs";

@Component({
  selector: "app-product-card-with-quantity",
  templateUrl: "./product-card-with-quantity.component.html",
  styleUrls: ["./product-card-with-quantity.component.scss"],
  imports: [UiKitModule, ProductImageComponent, LocalizeProductNamePipe, DisplayNamePipe, ReactiveFormsModule],
  standalone: true,
})
export class ProductCardWithQuantityComponent implements OnInit {
  private tooltipService = inject(ProductCardTooltipService);
  private productService = inject(ProductService);
  private userService = inject(UserService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly productData = input.required<Product>();
  readonly showCosts = input.required<boolean>();
  readonly selectionMode = input.required<"single" | "multi">();
  readonly imageUrl = input<string>();
  readonly fallbackImageUrl = input<string>();
  readonly quantity = input<number>(0);
  readonly preselectedQuantity = input<number | null>(null);
  readonly minValue = input<number>(0);

  readonly quantityChanged$ = output<number>();

  private readonly showZrefPrices = toSignal(this.userService.user$.pipe(map((user) => user.showZrefPrices)), {
    initialValue: false,
  });

  protected readonly product = computed(() => this.productData());
  protected readonly priceData = computed(() => {
    const showZref = this.showZrefPrices();
    const regularPrice = this.productService.getPriceData(this.product());
    const zrefPrice = this.productService.getZrefPriceData(this.product());
    if (showZref) {
      return zrefPrice;
    }
    return regularPrice;
  });
  protected readonly tooltipVisible = signal(false);
  protected readonly form = this.formBuilder.group({ quantity: [0, Validators.min(this.minValue())] });

  @ViewChild("checkbox")
  protected checkbox?: CheckBoxControl;

  constructor() {
    effect(() => {
      // trigger form selection once if preselected value
      const quantity = this.preselectedQuantity();
      if (quantity) {
        this.quantityChanged$.emit(quantity);
      }
    });
    effect(() => {
      const quantity = this.quantity();
      if (this.form.value.quantity !== quantity) {
        this.form.patchValue({ quantity }, { emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    this.tooltipService.visibleTooltip.subscribe(() => {
      this.tooltipVisible.set(false);
    });
    this.form.valueChanges.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (value.quantity === null || value.quantity === undefined) {
        return;
      }
      if (value.quantity < this.minValue() || isNaN(value.quantity)) {
        this.form.patchValue({ quantity: this.minValue() }, { emitEvent: false });
        this.onQuantityChanged(this.minValue());
        return;
      }
      this.onQuantityChanged(value.quantity);
    });
  }

  protected onTooltipClicked($event: MouseEvent) {
    if (this.isTooltipDisabled()) {
      return;
    }

    if (!this.tooltipVisible()) {
      this.tooltipService.tooltipSelected($event.currentTarget);
    }
    this.tooltipVisible.update((value) => !value);
  }

  protected isTooltipDisabled(): boolean {
    return this.product().name.length <= 80;
  }

  protected onCardClicked() {
    if (this.checkbox) {
      this.quantityChanged$.emit(this.checkbox.checked ? 0 : 1);
    }
  }

  protected onCheckedChanged(checked: boolean) {
    this.quantityChanged$.emit(checked ? 1 : 0);
  }

  protected increment() {
    this.onQuantityChanged(this.form.value.quantity! + 1);
  }

  protected decrement() {
    if (this.form.value.quantity! <= this.minValue()) return;
    this.onQuantityChanged(this.form.value.quantity! - 1);
  }

  private onQuantityChanged(quantity: number) {
    if (quantity !== this.quantity()) {
      this.quantityChanged$.emit(quantity);
    }
  }
}
